import { AxiosError } from 'axios'
import { useCallback } from 'react'

import { useQuery, UseQueryOptions } from '@tanstack/react-query'

import { CurrencyPaymentItem } from '@/interfaces/billing'

import APIServices from '../../api/APIServices'

/**
 * Query parameters for filtering and paginating currency payments
 */
export interface CurrencyPaymentsQueryParams {
  per_page?: number | null
  page?: number | null
  codeCurrency?: string | null
  dateFrom?: string | null
  dateTo?: string | null
  amountFrom?: string | null
  amountTo?: string | null
  economicCycleIds?: string | null
  paymentGatewayIds?: string | null
  areaSalesId?: string | null
  isPrepaidPayment?: boolean
  paymentConfirmed?: boolean
  isPartialPayment?: boolean
  isARefund?: boolean
}

// Unique query key for currency payments cache
export const CURRENCY_PAYMENTS_QUERY_KEY = 'currency-payments'

/**
 * Response type for currency payments query
 */
export interface CurrencyPaymentsQueryResponse {
  totalItems: number
  currentPage: number
  totalPages: number
  items: CurrencyPaymentItem[]
}

/**
 * Fetch currency payments from the API with optional filtering and pagination
 *
 * @param params - Optional query parameters for filtering and pagination
 * @returns Promise containing the paginated currency payments response
 */
const fetchCurrencyPayments = async (
  params?: CurrencyPaymentsQueryParams
): Promise<CurrencyPaymentsQueryResponse> => {
  // Filter out null/undefined values to clean up the request parameters
  const cleanParams = Object.fromEntries(
    Object.entries(params || {}).filter(([, value]) => value != null)
  )

  const response = await APIServices.get('/invoice/currencypayments', {
    params: cleanParams,
  })
  return response.data
}

/**
 * Custom hook for fetching currency payments with filtering and pagination
 *
 * @param params - Query parameters for filtering and paginating currency payments
 * @param options - TanStack Query options for customizing query behavior (caching, refetching, etc.)
 * @param businessId - The business ID to scope the query to
 * @returns Query result object containing data, loading states, pagination metadata, and utility functions
 */
export const useCurrencyPaymentsQuery = ({
  params,
  options,
  businessId,
}: {
  params?: CurrencyPaymentsQueryParams
  options?: Partial<UseQueryOptions<CurrencyPaymentsQueryResponse, AxiosError>>
  businessId: number
}) => {
  // Normalize params with sensible defaults for pagination
  const normalizedParams: CurrencyPaymentsQueryParams = {
    page: 1,
    per_page: 10,
    ...params,
  }

  // Query hook with reactive parameters and optimized caching
  const queryResult = useQuery<CurrencyPaymentsQueryResponse, AxiosError>({
    queryKey: getCurrencyPaymentsQueryKey({ businessId, normalizedParams }),
    queryFn: () => fetchCurrencyPayments(normalizedParams),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  })

  // Helper functions for pagination metadata and navigation
  const getCurrentPage = useCallback(() => {
    return normalizedParams.page || 1
  }, [normalizedParams.page])

  const getPageSize = useCallback(() => {
    return normalizedParams.per_page || 10
  }, [normalizedParams.per_page])

  const hasNextPage = useCallback(() => {
    if (!queryResult.data) return false
    const { currentPage, totalPages } = queryResult.data
    return currentPage < totalPages
  }, [queryResult.data])

  const hasPreviousPage = useCallback(() => {
    const currentPage = getCurrentPage()
    return currentPage > 1
  }, [getCurrentPage])

  return {
    ...queryResult,
    pagination: {
      currentPage: getCurrentPage(),
      pageSize: getPageSize(),
      hasNextPage: hasNextPage(),
      hasPreviousPage: hasPreviousPage(),
      totalPages: queryResult.data?.totalPages || 0,
      totalItems: queryResult.data?.totalItems || 0,
      params: normalizedParams,
    },
  }
}

/**
 * Helper function to generate the currency payments query key
 * Useful for cache invalidation, prefetching, or manual cache manipulation
 *
 * @param normalizedParams - The normalized query parameters
 * @param businessId - The business ID for cache scoping
 * @returns Array representing the unique query key for caching
 */
export const getCurrencyPaymentsQueryKey = ({
  businessId,
  normalizedParams,
}: {
  normalizedParams: CurrencyPaymentsQueryParams
  businessId: number
}) => [CURRENCY_PAYMENTS_QUERY_KEY, { businessId, ...normalizedParams }]
